// useBaseRailOrder.js

import { useCallback, useMemo } from 'react';

import { useUser } from 'context/UserContext';
import { createSetting, updateSetting } from 'services/settings/SettingWriteService';
import { getBaseKey } from 'services/bases/BaseReadService';
import {
  buildBaseRailOrderKeys,
  moveBaseInRailOrder,
  normalizeBaseRailOrderKeys,
  sortBasesByRailOrder,
} from 'utilities/BaseRailOrder';
import {
  buildBaseRailOrderSettingPatch,
  getBaseRailOrderFromSettings,
} from 'utilities/baseSettingsKeys';

async function persistBaseRailOrder(userKey, userSettings, nextOrderKeys) {
  const patch = buildBaseRailOrderSettingPatch(nextOrderKeys);

  try {
    if (userSettings?.id || userSettings?.userKey) {
      await updateSetting(userKey, patch);
      return;
    }

    await createSetting(userKey, patch);
  } catch (error) {
    if (error?.code === 'not-found') {
      await createSetting(userKey, patch);
      return;
    }

    throw error;
  }
}

/**
 * Applies and persists the user's base rail sort order.
 */
export default function useBaseRailOrder(bases) {
  const { user, userSettings, setUserSettings } = useUser();

  const savedOrderKeys = useMemo(() => {
    return normalizeBaseRailOrderKeys(getBaseRailOrderFromSettings(userSettings));
  }, [userSettings]);

  const orderedBases = useMemo(() => {
    return sortBasesByRailOrder(bases, savedOrderKeys, getBaseKey);
  }, [savedOrderKeys, bases]);

  const orderKeys = useMemo(() => {
    return buildBaseRailOrderKeys(orderedBases, savedOrderKeys, getBaseKey);
  }, [orderedBases, savedOrderKeys]);

  const reorderBases = useCallback(async (fromKey, toKey) => {
    const userKey = String(user?.key || '').trim();
    const normalizedFromKey = String(fromKey || '').trim();
    const normalizedToKey = String(toKey || '').trim();

    if (!userKey || !normalizedFromKey || !normalizedToKey || normalizedFromKey === normalizedToKey) {
      return false;
    }

    const previousOrderKeys = orderKeys;
    const nextOrderKeys = moveBaseInRailOrder(previousOrderKeys, normalizedFromKey, normalizedToKey);

    if (!nextOrderKeys) return false;

    setUserSettings?.((previous) => ({
      ...(previous || {}),
      userKey,
      ...buildBaseRailOrderSettingPatch(nextOrderKeys),
    }));

    try {
      await persistBaseRailOrder(userKey, userSettings, nextOrderKeys);
      return true;
    } catch (error) {
      console.error('[useBaseRailOrder] failed to save base rail order:', error);
      setUserSettings?.((previous) => ({
        ...(previous || {}),
        userKey,
        ...buildBaseRailOrderSettingPatch(previousOrderKeys),
      }));
      return false;
    }
  }, [orderKeys, setUserSettings, user?.key, userSettings]);

  return {
    canReorder: orderedBases.length > 1,
    orderedBases,
    reorderBases,
  };
}
